import { getCurrencyFromLocale, TravelTypeEnum } from '@repo/utils'
import React, { Dispatch, SetStateAction } from 'react'
import {
  Control,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { HouseSearchFilterSwitchBudget } from '../house-search-switch-budget/HouseSearchFilterSwitchBudget'
import { useLocale } from '../../../../hooks/use-locale'
import { RangeSlider } from '../../../atoms/range-slider/RangeSlider'
import { useTravelTypeContext } from '../../../contexts'
import { ISearchFormProps } from '../../../templates/house-search-layout/HouseSearchLayout'

interface IHouseSearchFilterSearchProps<T extends FieldValues> {
  control: Control<T, any>
  getValues: UseFormGetValues<ISearchFormProps>
  watch: UseFormWatch<ISearchFormProps>
  setValue: UseFormSetValue<ISearchFormProps>
  minName: string
  maxName: string
  title?: React.ReactNode
  minBudgetValue?: number
  maxBudgetValue?: number
  transientBudgetMin: number
  transientBudgetMax: number
  setTransientBudgetMin: Dispatch<SetStateAction<number>>
  setTransientBudgetMax: Dispatch<SetStateAction<number>>
  switchName: string
}

export const HouseSearchFilterBudget = <T extends FieldValues>({
  control,
  minName,
  maxName,
  title,
  minBudgetValue,
  maxBudgetValue,
  getValues,
  watch,
  setValue,
  transientBudgetMin,
  transientBudgetMax,
  setTransientBudgetMin,
  setTransientBudgetMax,
  switchName,
}: IHouseSearchFilterSearchProps<T>) => {
  const { travelType } = useTravelTypeContext()
  const { locale } = useLocale()
  const currency = getCurrencyFromLocale(locale)
  const intl = useIntl()

  return (
    <div className={s.container}>
      {title}
      <div className={s.subtitle}>
        /
        {travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT ? (
          <FormattedMessage
            id='ui.HouseSearchFilterBudget.person'
            defaultMessage='personne'
          />
        ) : (
          <>
            <FormattedMessage
              id='ui.HouseSearchFilterBudget.adults'
              defaultMessage='voyageur'
            />{' '}
            /
            {travelType === TravelTypeEnum.NIGHTLY_TRIP ? (
              <FormattedMessage
                id='ui.HouseSearchFilterBudget.night'
                defaultMessage='nuit'
              />
            ) : (
              <FormattedMessage
                id='ui.HouseSearchFilterBudget.day'
                defaultMessage='jour'
              />
            )}
          </>
        )}
      </div>
      {travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT ? (
        <HouseSearchFilterSwitchBudget
          control={control}
          name={switchName}
          rules={{}}
          label1={intl.formatMessage({
            id: 'ui.HouseSearchFilterBudget.fullBoard',
            defaultMessage: 'Pension complète',
          })}
          label2={intl.formatMessage({
            id: 'ui.HouseSearchFilterBudget.notFullBoard',
            defaultMessage: 'Location sèche',
          })}
          forceAllInclusive={travelType === TravelTypeEnum.DAILY_TRIP}
        />
      ) : null}

      <RangeSlider
        minName={minName}
        maxName={maxName}
        control={control}
        minLimit={minBudgetValue}
        maxLimit={maxBudgetValue || 500}
        initialMin={minBudgetValue}
        initialMax={maxBudgetValue}
        step={1}
        minUnit={currency.symbol}
        maxUnit={`${currency.symbol}+`}
        getValues={getValues}
        watch={watch}
        setValue={setValue}
        tempValueMin={transientBudgetMin}
        tempValueMax={transientBudgetMax}
        setTransientValueMax={setTransientBudgetMax}
        setTransientValueMin={setTransientBudgetMin}
      />
    </div>
  )
}
